import { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import ImageUpload from '@/components/admin/ImageUpload';
import { adminApi } from '@/lib/api';

const AdminSlides = () => {
  const [slides, setSlides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    title: '', subtitle: '', description: '', button_text: 'Get Started', button_link: '',
    secondary_button_text: 'Learn More', secondary_button_link: '/contact-us', image_url: '', order: 0, is_active: true
  });

  useEffect(() => { fetchSlides(); }, []);

  const fetchSlides = async () => {
    try { const res = await adminApi.getHeroSlides(); setSlides(res.data); }
    catch (error) { toast.error('Failed to fetch slides'); }
    finally { setLoading(false); }
  };

  const resetForm = () => {
    setFormData({ title: '', subtitle: '', description: '', button_text: 'Get Started', button_link: '',
      secondary_button_text: 'Learn More', secondary_button_link: '/contact-us', image_url: '', order: slides.length, is_active: true });
    setEditing(null);
  };

  const handleEdit = (s) => { setEditing(s); setFormData(s); setDialogOpen(true); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title || !formData.image_url) { toast.error('Title and image required'); return; }
    try {
      if (editing) { await adminApi.updateHeroSlide(editing.id, formData); toast.success('Updated'); }
      else { await adminApi.createHeroSlide(formData); toast.success('Created'); }
      setDialogOpen(false); resetForm(); fetchSlides();
    } catch (error) { toast.error('Failed to save'); }
  };

  const handleDelete = async (id) => {
    try { await adminApi.deleteHeroSlide(id); toast.success('Deleted'); fetchSlides(); }
    catch (error) { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-6" data-testid="admin-slides-page">
      <div className="flex items-center justify-between">
        <div><h1 className="text-3xl font-heading font-bold">Hero Slides</h1><p className="text-muted-foreground">Homepage slider content</p></div>
        <Dialog open={dialogOpen} onOpenChange={(o) => { setDialogOpen(o); if (!o) resetForm(); }}>
          <DialogTrigger asChild><Button><Plus className="w-4 h-4 mr-2" /> Add Slide</Button></DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? 'Edit' : 'New'} Slide</DialogTitle></DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2"><Label>Title *</Label><Input value={formData.title} onChange={(e) => setFormData(p => ({ ...p, title: e.target.value }))} /></div>
              <div className="space-y-2"><Label>Description</Label><Textarea value={formData.description} onChange={(e) => setFormData(p => ({ ...p, description: e.target.value }))} rows={3} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Button Text</Label><Input value={formData.button_text} onChange={(e) => setFormData(p => ({ ...p, button_text: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Button Link</Label><Input value={formData.button_link} onChange={(e) => setFormData(p => ({ ...p, button_link: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Secondary Button</Label><Input value={formData.secondary_button_text} onChange={(e) => setFormData(p => ({ ...p, secondary_button_text: e.target.value }))} /></div>
                <div className="space-y-2"><Label>Secondary Link</Label><Input value={formData.secondary_button_link} onChange={(e) => setFormData(p => ({ ...p, secondary_button_link: e.target.value }))} /></div>
              </div>
              <ImageUpload label="Slide Image *" value={formData.image_url} onChange={(url) => setFormData(p => ({ ...p, image_url: url }))} />
              <div className="flex gap-4">
                <div className="space-y-2"><Label>Order</Label><Input type="number" value={formData.order} onChange={(e) => setFormData(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} className="w-20" /></div>
                <div className="flex items-center gap-2 pt-6"><Switch checked={formData.is_active} onCheckedChange={(c) => setFormData(p => ({ ...p, is_active: c }))} /><Label>Active</Label></div>
              </div>
              <div className="flex justify-end gap-2"><Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button><Button type="submit">Save</Button></div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div className="space-y-4">
        {loading ? <div className="py-8 text-center">Loading...</div> : slides.length === 0 ? <p className="text-center py-8 text-muted-foreground">No slides yet</p> :
          slides.map((slide) => (
            <Card key={slide.id} data-testid={`slide-${slide.id}`}>
              <CardContent className="p-4 flex gap-4 items-center">
                <img src={slide.image_url} alt={slide.title} className="w-32 h-20 object-cover rounded" />
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="font-bold">{slide.title}</h3>
                    {slide.is_active ? <Eye className="w-4 h-4 text-green-500" /> : <EyeOff className="w-4 h-4 text-muted-foreground" />}
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-1">{slide.description}</p>
                </div>
                <div className="flex gap-1">
                  <Button variant="ghost" size="icon" onClick={() => handleEdit(slide)}><Edit className="w-4 h-4" /></Button>
                  <AlertDialog><AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="text-destructive"><Trash2 className="w-4 h-4" /></Button></AlertDialogTrigger>
                    <AlertDialogContent><AlertDialogHeader><AlertDialogTitle>Delete?</AlertDialogTitle></AlertDialogHeader><AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={() => handleDelete(slide.id)}>Delete</AlertDialogAction></AlertDialogFooter></AlertDialogContent>
                  </AlertDialog>
                </div>
              </CardContent>
            </Card>
          ))}
      </div>
    </div>
  );
};

export default AdminSlides;
